import * as Sentry from '@sentry/angular';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { SESSION_EXPIRED_ERROR_NAME } from '../../user/session-expired.error';

/**
 * GA4 property. One gtag/js load serves every id configured below.
 *
 * Mirrored in the inline gate in `src/index.html`, which is what actually
 * requests the tag. analytics-gate.spec.ts fails if the two drift.
 */
export const GA4_MEASUREMENT_ID = 'G-43GG48V9RH';

/**
 * Google Ads account behind the paid campaigns pointed at /sales-forecast/*.
 *
 * Configured on every page, not just the ones that convert: this is what
 * turns the `gclid` on the ad's landing URL into the first-party `_gcl_aw`
 * cookie. The purchase happens later, on another page and after a round trip
 * out to Stripe and back, and that cookie is the only thing tying the two
 * together.
 */
export const GOOGLE_ADS_ID = 'AW-18370774239';

/** Hotjar site and snippet version, straight from its install snippet. */
const HOTJAR_SITE_ID = 3729323;
const HOTJAR_SNIPPET_VERSION = 6;

export const ANALYTICS_DEBUG_KEY = 'analytics_debug';

/**
 * Whether this browser has opted into loading the analytics tags outside
 * production — the only way to verify a conversion without spending on ads.
 *
 * `?analytics=debug` turns it on and it then persists, because the tags load
 * once at app start and the URL that enabled them is gone by the next
 * navigation. `?analytics=off` clears it. The index.html gate reads and
 * writes the same key, so one query parameter arms both halves.
 *
 * @param search defaults to the current query string; passed explicitly in
 *   tests, and the whole thing is guarded because localStorage throws in
 *   some privacy modes.
 */
export function analyticsDebugEnabled(
  search: string = typeof location === 'undefined' ? '' : location.search
): boolean {
  try {
    const requested = new URLSearchParams(search).get('analytics');
    if (requested === 'debug')
      localStorage.setItem(ANALYTICS_DEBUG_KEY, 'true');
    if (requested === 'off') localStorage.removeItem(ANALYTICS_DEBUG_KEY);
    return localStorage.getItem(ANALYTICS_DEBUG_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * The half of the analytics stack that is allowed to be late.
 *
 * gtag is not here, and this service never requests it. The inline gate in
 * `src/index.html` owns it end to end: it installs the `dataLayer` queue and
 * issues the `config` calls during HTML parse, which is the half that has to
 * be early, and appends the tag itself from the first idle gap. What was
 * wrong with loading it from here was the *dependency* - the queue did not
 * exist until 420 KB of JavaScript had downloaded, parsed and bootstrapped
 * Angular, and an event fired before that had nowhere to go. Once the queue
 * is installed at parse time, when the tag arrives stops mattering, so it
 * arrives after the paint. See analytics-gate.spec.ts for what pins the two
 * ids and the environment check to the constants above.
 *
 * This service's only remaining business with gtag is `trackGA`, which
 * pushes into that queue and no-ops when the gate decided this document does
 * not report - a hostname outside the allowlist, or a non-production build
 * without the debug flag.
 *
 * What is left is Hotjar, where a lost session recording is cosmetic, and
 * Sentry's session replay, which must not be running while the page is
 * still painting. Both wait for the first idle period.
 */
@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  private browser = false;
  private configured = false;
  private hotjarReady = false;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private zone: NgZone
  ) {
    this.browser = isPlatformBrowser(platformId);
  }

  /**
   * Longest the deferred work may wait. Only ever reached on a main thread
   * that never falls idle; the normal case is the first gap after paint.
   */
  private static IDLE_DEADLINE_MS = 2_000;

  /**
   * Returns void, and has to keep returning void: this runs as an
   * APP_INITIALIZER (see main.ts), and Angular holds the first render back
   * for as long as an initializer's promise is pending.
   */
  init(): void {
    if (!this.browser) return;
    if (this.configured) return;
    this.configured = true;

    if (!environment.production && !analyticsDebugEnabled()) return;

    this.configureHotjar();

    // Fire and forget: by design nothing in the app awaits any of this.
    //
    // Outside the Angular zone, so that neither the idle callback nor the
    // script onload handlers it goes on to register can trigger a change
    // detection pass over the whole app - which would land right after the
    // paint this method exists to protect. Nothing in here touches app
    // state, so there is nothing for Angular to notice.
    this.zone.runOutsideAngular(() =>
      this.whenIdle(() => {
        void this.loadHotjar();
        // Sentry is initialised before bootstrap (handleIntegrations), but
        // its replay recorder is added here instead, because "after the
        // first paint, off the Angular zone" is exactly what this hook is
        // and rrweb has no business instrumenting the first render.
        if (environment.production) void loadSentryReplay();
      })
    );
  }

  /** Hotjar's command queue, drained by its tag once that loads. */
  private configureHotjar(): void {
    const w = window as any;
    w.hj =
      w.hj ||
      function () {
        (w.hj.q = w.hj.q || []).push(arguments);
      };
    w._hjSettings = { hjid: HOTJAR_SITE_ID, hjsv: HOTJAR_SNIPPET_VERSION };
  }

  /**
   * Runs `work` in the browser's first idle period - past the first paint,
   * so these requests never compete for bandwidth with what the visitor is
   * waiting to see - and within IDLE_DEADLINE_MS come what may, because a
   * page that stays busy still has to end up instrumented.
   */
  private whenIdle(work: () => void): void {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => work(), {
        timeout: IntegrationsService.IDLE_DEADLINE_MS,
      });
      return;
    }
    // Safari before 16.4. A frame, then a macrotask: rAF alone runs before
    // the paint it is scheduled for, and a bare setTimeout(0) queued during
    // bootstrap can land in the macrotask right after it, with no paint in
    // between.
    requestAnimationFrame(() => setTimeout(work, 0));
  }

  /**
   * static.hotjar.com is blocked by every common ad blocker, so a rejection
   * here is an ordinary outcome for a large share of visitors rather than an
   * exception. console.debug and not console.error on purpose: Sentry's
   * captureConsoleIntegration reports the error level, and a blocked tag is
   * not something to page anyone about.
   */
  private async loadHotjar(): Promise<void> {
    if (this.hotjarReady) return;

    try {
      await this.appendScript(
        `https://static.hotjar.com/c/hotjar-${HOTJAR_SITE_ID}.js?sv=${HOTJAR_SNIPPET_VERSION}`,
        { async: true, crossOrigin: 'anonymous' as any }
      );
      this.hotjarReady = true;
    } catch (e) {
      console.debug('Hotjar failed to load (likely blocked):', e);
    }
  }

  private appendScript(
    src: string,
    attrs: Partial<HTMLScriptElement> = {}
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      Object.assign(s, attrs);
      s.onload = () => resolve();
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  track(
    event: string,
    params?: Record<string, any>,
    opts: { ga?: boolean } = { ga: true }
  ) {
    if (opts.ga) this.trackGA(event, params);
  }

  /**
   * Pushes into the queue the index.html gate installed. That queue is a
   * plain array which gtag.js replays, in order, whenever it executes, so an
   * event fired before the tag arrives is held rather than dropped - which
   * is what the Stripe success page depends on, reporting its purchase
   * conversion during its own first render.
   *
   * No queue means the gate decided this document does not report, and the
   * event is dropped on purpose.
   */
  trackGA(event: string, params?: Record<string, any>) {
    if (!(window as any).gtag) return;
    (window as any).gtag('event', event, params);
  }
}

/**
 * True when every exception in the event was thrown by a script that has no
 * URL - injected code, in practice, since everything we ship has a filename.
 *
 * An in-app browser opens a page inside its own webview and runs its own
 * scripts against it. TikTok's Android webview carries a performance probe
 * that polls for navigation timings and, on Android 10, reads them off
 * something undefined: "Cannot read properties of undefined (reading
 * 'domInteractive')", 26 events and climbing from /sales-forecast/restaurant,
 * which is where paid traffic lands and so where in-app browsers arrive. It
 * reaches us because zone.js patches setTimeout for the whole document, not
 * just for Angular's own code, so the probe's polling callback runs inside the
 * zone and its failure arrives at our ErrorHandler indistinguishable from ours
 * - and the frames underneath it really are ours, which is why filtering on
 * "any frame from our bundle" would not have dropped a thing.
 *
 * The frame that threw is what settles it, and frames run oldest-first, so it
 * is the last one. Injected and eval'd code is reported as `<anonymous>`;
 * everything of ours has a bundle URL, index.html's inline scripts included
 * (they carry the document's URL), and a library we deliberately load carries
 * its CDN URL, so a genuine mapbox-gl.js failure is still reported.
 */
function thrownByInjectedScript(event: Sentry.ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;

  return values.every((value) => {
    const frames = value.stacktrace?.frames;
    if (!frames?.length) return false;
    const filename = frames[frames.length - 1].filename;
    return !filename || filename === '<anonymous>';
  });
}

/**
 * A session that ran out, which is a state of the world rather than a fault.
 *
 * The visitor gets the sign-in modal `AuthInterceptor` opens; nobody needs to
 * be paged. Before this, one expired session on /chat reported the refresh
 * endpoint's 400 as an unhandled exception - the loudest issue we had.
 */
function isExpiredSession(event: Sentry.ErrorEvent): boolean {
  return !!event.exception?.values?.some(
    (value) => value.type === SESSION_EXPIRED_ERROR_NAME
  );
}

/**
 * Sentry's configuration, exported as data so it can be asserted on without
 * spying on the module namespace.
 */
export const SENTRY_OPTIONS: Sentry.BrowserOptions = {
  dsn: 'https://60236da900aa8e3fd1eba789807da88e@o4505992663793664.ingest.sentry.io/4505993237692416',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
    // replayIntegration is deliberately absent; see loadSentryReplay.
  ],
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', 'https://interactive-map-ai.com/'],
  // Performance Monitoring. One transaction in ten: this was 1.0, under a
  // comment reading "reduce in production!", so every route change and every
  // HTTP call on every session was spanned and sent.
  tracesSampleRate: 0.1,
  // Session Replay. Both rates still apply once the recorder is added below.
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend: (event) =>
    thrownByInjectedScript(event) || isExpiredSession(event) ? null : event,
};

export function handleIntegrations(): void {
  Sentry.init(SENTRY_OPTIONS);
}

/**
 * Adds session replay after the first paint, rather than at init().
 *
 * Two things were wrong with having `replayIntegration()` in the list above.
 * It is a static import, so rrweb - roughly half of the 249 KB raw Sentry
 * chunk - sat in the eager bundle on the critical path. And with
 * `replaysOnErrorSampleRate: 1.0` the recorder arms in buffer mode for
 * *every* session, which put rrweb's MutationObserver on the main thread
 * serialising Angular's entire first render, inside the LCP window.
 *
 * lazyLoadIntegration fetches the recorder from browser.sentry-cdn.com, so
 * it is one more origin at runtime, but only after the page is painted and
 * only for the sessions the sample rates keep. A failure is not worth
 * reporting: it costs a replay, and nothing else.
 */
export async function loadSentryReplay(): Promise<void> {
  try {
    const replayIntegration =
      await Sentry.lazyLoadIntegration('replayIntegration');
    Sentry.addIntegration(replayIntegration());
  } catch (e) {
    console.debug('Sentry session replay failed to load:', e);
  }
}
